/**
 * `<caos-button-group>` — a run of buttons joined into one control.
 * (Catalog: Actions › ButtonGroup — a run of buttons joined into one control.)
 *
 * CAOS-599. This is the only genuinely new piece in a survey that folded three separate button
 * components into modes and states of the one we already had. A joined run is not a colour, a size
 * or a state of a button — it is an arrangement of several of them, and there was nowhere for it to
 * live. `<caos-action-bar>` arranges actions and collapses the spare ones behind an overflow, but it
 * leaves them as separate controls with space between; a joined run reads as one thing with several
 * ways to press it, which is a different statement to a reader.
 *
 * What it actually does is small and deliberately so:
 *
 * - It JOINS. The corners between neighbours are squared, the hairlines between them collapse onto
 *   one another instead of drawing double, and whichever button is under the pointer or holding
 *   focus comes forward so its border and focus ring are drawn whole rather than half-covered.
 * - It makes them AGREE ON A HEIGHT. The group's `size` is set on every button in it, so a run
 *   cannot be assembled out of buttons at three different heights. The height itself comes from the
 *   platform size scale, which is the same place the button takes it from on its own.
 *
 * It does NOT decide what the buttons do, which one is pressed, or whether they are exclusive. A
 * group of toggles is a group of buttons each carrying its own `pressed` state, and a row of
 * mutually exclusive filters is `<caos-segmented-filter>`, which is about what is SHOWN rather than
 * about doing something.
 *
 * For a screen reader the group is a `group` with a name from `label`. That is the honest reading:
 * a `toolbar` promises arrow-key navigation between its controls, and a joined run of ordinary
 * buttons is still a run of ordinary buttons that Tab moves through one at a time.
 *
 * Styling seam: `::part(group)`; the buttons are the default slot.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { applyAccessibleName } from '../accessible-name.js';
import { CONTROL_STEPS, type ControlStep } from '../sizes.js';

/** The step a group draws at when `size` is absent or names no step. */
export const BUTTON_GROUP_DEFAULT_SIZE: ControlStep = 'md';

/** The group’s own styles, as written. */
export const BUTTON_GROUP_STYLES = `
  :host { display: inline-flex; max-width: 100%; vertical-align: middle; }
  :host([block]) { display: flex; }

  .group {
    display: inline-flex;
    align-items: stretch;
    max-width: 100%;
  }
  :host([block]) .group { display: flex; width: 100%; }

  /* Each button sits in the flow so it can be brought forward, and squares off the corners it
     shares with a neighbour. The radius is read by the button through these two properties, which
     are the only seam a parent has into a child's shadow root: a custom property inherits through
     the boundary where a selector cannot reach across it.

     Said as START and END rather than left and right, in both the corner names and the margin, so a
     run reads correctly in a right-to-left script: there the first button is the rightmost one, and
     a rule written in physical directions would round the wrong two corners and collapse the
     hairline on the wrong edge. */
  ::slotted(caos-button) { position: relative; }
  ::slotted(caos-button:not(:first-child)) { margin-inline-start: -1px; }
  ::slotted(caos-button:not(:first-child)) { --caos-button-radius-start: 0; }
  ::slotted(caos-button:not(:last-child)) { --caos-button-radius-end: 0; }

  /* The collapsed hairline belongs to whichever button the eye is on. Without this the button on
     the right of a pair draws its left border UNDER its neighbour's right one, so a hover or a
     focus ring is clipped down one side and the control looks broken rather than focused. */
  ::slotted(caos-button:hover),
  ::slotted(caos-button:focus-within),
  ::slotted(caos-button[pressed="true"]),
  ::slotted(caos-button[pressed=""]) { z-index: 1; }

  /* A group that fills its container shares the width out evenly rather than letting the longest
     label take it. Buttons keep their own width otherwise. */
  :host([block]) ::slotted(caos-button) { flex: 1 1 0; }
`;

const sheet = makeSheet(BUTTON_GROUP_STYLES);

export class CaosButtonGroup extends CaosElement {
  static get observedAttributes(): string[] {
    return ['size', 'label'];
  }

  private readonly group: HTMLDivElement;
  private readonly buttonSlot: HTMLSlotElement;

  constructor() {
    super(sheet);
    this.group = document.createElement('div');
    this.group.className = 'group';
    this.group.setAttribute('part', 'group');
    this.group.setAttribute('role', 'group');
    this.buttonSlot = document.createElement('slot');
    // The buttons arrive after the element does — always, when the page is assembled from stored
    // metadata — so the size is applied on every slot change rather than once at construction.
    this.buttonSlot.addEventListener('slotchange', () => this.sizeTheButtons());
    this.group.append(this.buttonSlot);
    this.root.append(this.group);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    applyAccessibleName(this.group, this.getAttribute('label'), null);
    this.sizeTheButtons();
  }

  /** The step this group draws at: its own `size` when it names one, otherwise the default. */
  get step(): ControlStep {
    const named = (this.getAttribute('size') ?? '').trim() as ControlStep;
    return CONTROL_STEPS.includes(named) ? named : BUTTON_GROUP_DEFAULT_SIZE;
  }

  /**
   * Put the group's step on every button in it.
   *
   * This overwrites a size an author set on an individual button, and that is the point rather than
   * a side effect: buttons at two different heights joined along one edge is not a control, it is a
   * drawing mistake. The group is where the decision belongs because the group is the thing that
   * has to line up.
   */
  private sizeTheButtons(): void {
    const step = this.step;
    // The group's own children rather than the slot's assignment, because assignment is computed
    // lazily and asking the slot while the element is being connected can answer with an empty list,
    // leaving the run mixed. The one place the two sets differ is a child asking for a named slot
    // this component does not have: it is not drawn in the run at all, so it is not the run's to
    // size, and silently resizing something that is not on screen is a change nobody can see.
    for (const el of Array.from(this.children)) {
      if (el.localName !== 'caos-button') continue;
      // An EMPTY slot attribute names the default slot, so `slot=""` is a button in this run and
      // not one asking to be somewhere else. Testing for the attribute's presence would skip it.
      if ((el.getAttribute('slot') ?? '') !== '') continue;
      el.setAttribute('size', step);
    }
  }
}

define('caos-button-group', CaosButtonGroup);

declare global {
  interface HTMLElementTagNameMap {
    'caos-button-group': CaosButtonGroup;
  }
}
